import { useEffect, useState } from 'react';
import type { BibleLocation, ReadingLevel, WritingLevel, WordSelection } from '@skjv/common';
import {
  tokenizeVerse,
  autoPickWords,
  renderWordSelection,
  isSelectionValid,
  deriveWritingText,
} from '@skjv/common';
import { getVerseText } from '@/services/verse-text';

interface WordPickEditorProps {
  startingLocation: BibleLocation;
  endingLocation?: BibleLocation;
  readingLevel: ReadingLevel;
  writingLevel: WritingLevel;
  wordSelection?: WordSelection;
  onPick: (selection: WordSelection, fragment: string) => void;
}

const SLIDER_MAX = 8;

/**
 * Word picker for ONE_WORD / SEVERAL_WORDS readers: shows the dropped
 * verse as tappable words with an auto-picked starting run (first content
 * word, stopwords skipped). Tap sets THE word (one) or the run's start
 * (several); the slider sets the run length. Every pick reports both the
 * structured selection and the rendered fragment the worksheet prints.
 */
export function WordPickEditor({
  startingLocation,
  endingLocation,
  readingLevel,
  writingLevel,
  wordSelection,
  onPick,
}: WordPickEditorProps) {
  const [words, setWords] = useState<string[] | null>(null);
  const isOneWord = readingLevel === 'ONE_WORD';

  // Fetch + tokenize the dropped verse (chapter HTML is cached)
  useEffect(() => {
    let isStale = false;
    setWords(null);
    getVerseText(startingLocation, endingLocation)
      .then((text) => {
        if (!isStale) setWords(tokenizeVerse(text));
      })
      .catch(() => {
        if (!isStale) setWords([]);
      });
    return () => {
      isStale = true;
    };
  }, [startingLocation, endingLocation]);

  // Auto-pick when there is no (or a stale, out-of-range) selection
  useEffect(() => {
    if (!words || words.length === 0) return;
    if (wordSelection && isSelectionValid(words, wordSelection)) return;
    const picked = autoPickWords(words, isOneWord ? 1 : 3);
    onPick(picked, renderWordSelection(words, picked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words, wordSelection, isOneWord]);

  if (!words) {
    return (
      <p className="mb-3 text-xs italic text-stone-400 dark:text-stone-500">Loading verse…</p>
    );
  }
  if (words.length === 0) {
    return (
      <p className="mb-3 text-xs italic text-stone-400 dark:text-stone-500">
        Could not load the verse text.
      </p>
    );
  }

  const selection =
    wordSelection && isSelectionValid(words, wordSelection)
      ? wordSelection
      : autoPickWords(words, isOneWord ? 1 : 3);
  const fragment = renderWordSelection(words, selection);
  const sliderMax = Math.min(SLIDER_MAX, words.length);

  const pickStart = (index: number) => {
    const wordCount = isOneWord ? 1 : selection.wordCount;
    const startWordIndex = Math.max(0, Math.min(index, words.length - wordCount));
    const picked = { startWordIndex, wordCount };
    onPick(picked, renderWordSelection(words, picked));
  };

  const pickCount = (count: number) => {
    const wordCount = Math.max(1, Math.min(count, words.length));
    const startWordIndex = Math.max(
      0,
      Math.min(selection.startWordIndex, words.length - wordCount),
    );
    const picked = { startWordIndex, wordCount };
    onPick(picked, renderWordSelection(words, picked));
  };

  const isInRun = (index: number) =>
    index >= selection.startWordIndex && index < selection.startWordIndex + selection.wordCount;

  return (
    <div className="mb-3" data-testid="word-pick-editor">
      <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
        {isOneWord ? 'Tap the word to read' : 'Tap where the words start'}
      </label>

      {/* Tappable verse words */}
      <div className="mb-2 flex flex-wrap gap-1 rounded-lg border border-stone-300/50 bg-white/80 p-2 dark:border-stone-600/50 dark:bg-stone-800/60">
        {words.map((word, index) => (
          <button
            key={index}
            type="button"
            onClick={() => pickStart(index)}
            data-testid={`word-chip-${index}`}
            data-picked={isInRun(index) || undefined}
            className={`rounded px-1.5 py-0.5 font-serif text-sm transition-colors ${
              index === selection.startWordIndex
                ? 'bg-cyan-500 font-semibold text-white'
                : isInRun(index)
                  ? 'bg-cyan-200 text-cyan-900 dark:bg-cyan-500/40 dark:text-cyan-100'
                  : 'text-stone-600 hover:bg-cyan-100 dark:text-stone-300 dark:hover:bg-cyan-900/40'
            }`}
          >
            {word}
          </button>
        ))}
      </div>

      {/* Run-length slider (several words only) */}
      {!isOneWord && (
        <div className="mb-2 flex items-center gap-3">
          <label className="text-xs text-stone-500 dark:text-stone-400" htmlFor="word-count-slider">
            Words
          </label>
          <input
            id="word-count-slider"
            type="range"
            min={2}
            max={sliderMax}
            value={Math.min(selection.wordCount, sliderMax)}
            onChange={(e) => pickCount(parseInt(e.target.value, 10))}
            className="flex-1 accent-cyan-500"
            data-testid="word-count-slider"
          />
          <span className="min-w-[4.5rem] text-right text-xs font-semibold tabular-nums text-cyan-600 dark:text-cyan-400">
            {selection.wordCount} {selection.wordCount === 1 ? 'word' : 'words'}
          </span>
        </div>
      )}

      {/* Worksheet preview */}
      <p className="text-xs text-stone-500 dark:text-stone-400" data-testid="word-pick-preview">
        Reads: <span className="font-serif font-semibold text-stone-700 dark:text-stone-200">{fragment}</span>
        {writingLevel === 'ONE_WORD' && !isOneWord && (
          <span className="ml-2 text-amber-600 dark:text-amber-400">
            ✏️ writes the first word: {deriveWritingText(fragment, writingLevel)}
          </span>
        )}
      </p>
    </div>
  );
}
